import { Component, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Config } from '@config';
import { LoaderService } from '@loader';
import { LoaderService as ScriptLoader, FileTypes } from '@tools';
import { MessagesService, MessageType } from '@ui-messages';
import { __i18n, LanguageService } from '@localisation';
import { AuthHttp } from '@http';
import { UrlEnhancerService } from '@url';
import { ContentLoader } from '@winforms';
import { HttpParams } from '@angular/common/http';
import { map, timeout } from 'rxjs/operators';
import { ButtonStates } from '@forms/clx-forms/controls/clx.button.component';
import { DataChangesHandlerService } from '@check'
import { lastValueFrom } from 'rxjs';
const win = window as any;

@Component({
    selector: 'scheduler-page',
    templateUrl: './scheduler.template.html',
    standalone: false
})
export class SchedulerComponent implements AfterViewInit {

    isLoading: boolean = true;
    schedulers: any = null;
    hasSchedulers: boolean = false;
    saveState: ButtonStates = null;

    constructor(
        public config: Config,
        private loader: LoaderService,
        private messageService: MessagesService,
        private urlEnhancer: UrlEnhancerService,
        private http: AuthHttp,
        private scriptLoader: ScriptLoader,
        private languageService: LanguageService,
        private contentLoaderService: ContentLoader,
        private dataChangesHandlerService: DataChangesHandlerService
    ) {
    }

    private async fetchCommandStatus(commandId: number, resolve: () => void, reject: (reason?: any) => void) {
        try {
            const data = await lastValueFrom(this.http.post(this.urlEnhancer.replaceParams(
                this.config.services.plant.plantSchedulersGetDataPointCommandStatus, {commandId}), null)
                .pipe(map((e: any) => {
                    if (e instanceof HttpResponse) {
                        return JSON.parse(e.body);
                    }
                })));
            switch (data.Status) {
                case -1:
                case 0:
                    setTimeout(() => this.fetchCommandStatus(commandId, resolve, reject), 1000);
                    break;

                case 1:
                    if (typeof data.Result === 'string') {
                        const result = JSON.parse(data.Result);
                        if (result.success) {
                            resolve();
                        } else {
                            if (Array.isArray(result.descr)) {
                                let lngIdx = this.schedulers.languageIndex;
                                if (lngIdx < 0 || lngIdx >= result.descr.length || !result.descr[lngIdx]) {
                                    lngIdx = 0;
                                }
                                reject(result.descr[lngIdx]);
                            }
                        }
                    } else {
                        setTimeout(() => this.fetchCommandStatus(commandId, resolve, reject), 1000);
                    }
                    break;

                case 2:
                    reject(__i18n('The command timed out.', 'operating.scheduler.status.text.commandtimedout'));
                    break;

                default:
                    reject(__i18n('Unexpected result received.', 'operating.scheduler.status.text.unexpectedresult'));
            }
        } catch (e) {
            reject(e);
        }
    }

    private async resolveCommandStatus(commandId: number): Promise<void> {
        const p = new Promise<void>((resolve, reject) => {
            this.fetchCommandStatus(commandId, resolve, reject);
        });
        return p;
    }

    public async save() {
        this.messageService.deleteTargetMessages('scheduler-save-message');
        const loaderId = this.loader.start();
        this.loader.setText(__i18n('Saving schedulers, please wait...', 'operating.scheduler.loader.text.savingschedulers'));
        this.saveState = ButtonStates.loading;
        try {
            const result = await lastValueFrom(this.http.post(this.urlEnhancer.replaceParams(this.config.services.plant.plantSchedulersSave), {})
                .pipe(map((e: any) => {
                    if (e instanceof HttpResponse) {
                        return JSON.parse(e.body);
                    }
                })));
            if (typeof result.commandIds === 'undefined' && !win.parseAjaxResult(result)) {
                this.messageService.addMessage({
                    target: 'scheduler-save-message',
                    type: MessageType.error,
                    text: __i18n('Unexpected result received.', 'operating.scheduler.status.text.unexpectedresult')
                });
                this.loader.stop(loaderId);
                this.saveState = null;
            } else {
                if (Array.isArray(result.commandIds) && result.commandIds.length > 0) {
                    const promises = result.commandIds.map(cid => this.resolveCommandStatus(cid));
                    try {
                        await Promise.all(promises);
                        this.messageService.addMessage({
                            target: 'scheduler-save-message',
                            type: MessageType.success,
                            text: __i18n('The scheduler data was successfully saved.', 'operating.scheduler.message.successfullysaved')
                        });
                        this.dataChangesHandlerService.markAsClean();
                        this.loader.stop(loaderId);
                        const reloaderId = this.loader.start();
                        this.loader.setText(__i18n('Reloading scheduler data, please wait.', 'operating.scheduler.loader.text.reloadingschedulers'));
                        setTimeout(async() => {
                            try {
                                await this.reloadSchedulerData(true, true);
                            } catch (e) {
                                // ignored
                            }
                            this.loader.stop(reloaderId);
                            this.saveState = null;
                        }, 5000);
                    } catch (ex) {
                        this.messageService.addMessage({
                            target: 'scheduler-save-message',
                            type: MessageType.error,
                            text: typeof ex === 'string' ? ex : __i18n('Failed to save the scheduler data. Please try again.', 'operating.scheduler.message.savefailed')
                        });
                        this.loader.stop(loaderId);
                        this.saveState = null;
                    }
                } else {
                    this.messageService.addMessage({
                        target: 'scheduler-save-message',
                        type: MessageType.info,
                        text: __i18n('There are no changes to save.', 'operating.scheduler.message.nochangestosave')
                    });
                    this.loader.stop(loaderId);
                    this.saveState = null;
                }
                if (result.warningForUnsavedObjects) {
                    this.messageService.addMessage({
                        type: MessageType.info,
                        text: __i18n('One or more schedulers cannot be saved. You do not have sufficient permissions.',
                            'operating.scheduler.message.schedulercannotbesaved')
                    });
                }
            }
        } catch (e) {
            this.messageService.addMessage({
                target: 'scheduler-save-message',
                type: MessageType.error,
                text: typeof e === 'string' ? e : __i18n('Cannot save schedulers data. Please try again.', 'operating.scheduler.status.text.cannotsaveschedulerdata')
            });
            this.loader.stop(loaderId);
            this.saveState = null;
        }
    }

    async ngAfterViewInit(): Promise<void> {

        const loaderId = this.loader.start();
        this.loader.setText(__i18n('loading schedulers',  'operating.scheduler.loader.text.loadingschedulers'));

        await this.contentLoaderService.loadMvcScripts();
        await this.scriptLoader.loadFiles([
            '/bundles/ba/scheduler',
            this.languageService.language.indexOf('de') === 0
                ? '/bundles/ba/scheduler/de'
                : '/bundles/ba/scheduler/en'
        ], FileTypes.script);

        this.initBaScheduler();
        this.loader.stop(loaderId);

        this.reloadSchedulerData(true, true);
    }

    private async reloadSchedulerData(forceReload: boolean, bypassCache: boolean): Promise<void> {
        const loaderId = this.loader.start();
        this.loader.setText(__i18n('loading schedulers',  'operating.scheduler.loader.text.loadingschedulers'));
        this.isLoading = true;

        try {
            this.schedulers = await lastValueFrom(this.http.getJSON(this.urlEnhancer.replaceParams(this.config.services.plant.plantSchedulers, {bypasscache: bypassCache})));
            this.hasSchedulers = !!this.schedulers?.clientRepresentationModel?.operateModel?.schedulerModel?.length;
            this.loader.stop(loaderId);
            this.isLoading = false;
        } catch(e) {
            this.schedulers = null;
            this.hasSchedulers = false;
            this.loader.stopAll();
            this.isLoading = false;
            this.messageService.addMessage({
                type: MessageType.error,
                text: __i18n('An error occurred while fetching the schedulers data.', 'operating.scheduler.message.errorfetchingschedulersdata')
            });
        }
        if (this.schedulers !== null) {
            const $ = win.$;
            const schedulerWidget = $('#scheduler-overview').data().widget;
            if (schedulerWidget === undefined) {
                const tool = $('#tools-area').addWidget('toolsArea').data().widget;
                $('#scheduler-overview').addWidget('schedulerView', {
                    viewModel: this.schedulers,
                    toolsArea: tool
                });
            } else {
                schedulerWidget.updateView(this.schedulers, forceReload);
            }
        }
    }

    private postify(data: any): HttpParams {
        let result = new HttpParams();

        const buildResult = (object, prefix) => {
            for (const key in object) {
                if (object.hasOwnProperty(key)) {
                    const postKey = isFinite(Number(key))
                        ? (prefix !== '' ? prefix : '') + '[' + key + ']'
                        : (prefix !== '' ? prefix + '.' : '') + key;

                    switch (typeof (object[key])) {
                        case 'number': case 'string': case 'boolean':
                            result = result.set(postKey, object[key]);
                            break;

                        case 'object':
                            if (object[key]) {
                                if (object[key].toUTCString) {
                                    result = result.set(postKey, object[key].toUTCString().replace('UTC', 'GMT'));
                                } else {
                                    buildResult(object[key], postKey !== '' ? postKey : key);
                                }
                            }
                    }
                }
            }
        };

        buildResult(data, '');

        return result;
    }

    private async serverOperation(urlPattern: string, data: any) {
        this.messageService.deleteTargetMessages('scheduler-save-message');
        let loaderId;
        const schedulerWidget = $('#scheduler-overview').data().widget;
        const body = this.postify(data);
        try {
            loaderId = this.loader.start();
            const headers = new HttpHeaders({'Content-type': 'application/x-www-form-urlencoded'});
            const schedulerModel = await lastValueFrom(this.http.post(this.urlEnhancer.replaceParams(urlPattern), body.toString(), {headers})
                .pipe(map((e: any) => {
                    if (e instanceof HttpResponse) {
                        return JSON.parse(e.body);
                    }
                })));
            if (!win.parseAjaxResult(schedulerModel)) {
                this.schedulers = schedulerModel;
                this.hasSchedulers = !!this.schedulers?.clientRepresentationModel?.operateModel?.schedulerModel?.length;
                this.dataChangesHandlerService.markAsDirty();
            }
            schedulerWidget.updateView(this.schedulers, false);
            this.loader.stop(loaderId);
        } catch (e) {
            this.loader.stop(loaderId);
            this.messageService.addMessage({
                type: MessageType.error,
                text: __i18n('The operation failed. Please try again.', 'operating.scheduler.message.operationfailed')
            });
            schedulerWidget.updateView(this.schedulers, false);
            throw e;
        }
    }

    private initBaScheduler() {
        const $ = win.jQuery;

        $.ba.scheduler = () => {
            return {
                setPhaseForDay: async(schedulerId, day, startTime, endTime, value) => {
                    const body = { schedulerId, day, startTime, endTime, value };
                    await this.serverOperation(this.config.services.plant.plantSchedulersSetPhaseForDay, body);
                },
                changePhaseForDay: async(schedulerId, day, prevStartTime, newStartTime, newEndTime, value) => {
                    const body = {schedulerId, day, prevStartTime, newStartTime, newEndTime, value};
                    await this.serverOperation(this.config.services.plant.plantSchedulersChangePhaseForDay, body);
                },
                deletePhaseForDay: async(schedulerId, day, startTime) => {
                    const body = {schedulerId, day, startTime};
                    await this.serverOperation(this.config.services.plant.plantSchedulersDeletePhaseForDay, body);
                },
                copyDailyProfile: async(schedulerId, sourceDay, destinationDays) => {
                    const body = {schedulerId, sourceDay, destinationDays};
                    await this.serverOperation(this.config.services.plant.plantSchedulersCopyDailyProfile, body);
                },
                requestExceptionMonths: async(schedulerId, startYear, startMonth, numberOfMonths, completehandler) => {
                    const body = {schedulerId, startYear, startMonth, numberOfMonths, completehandler};
                    try {
                        await this.serverOperation(this.config.services.plant.plantSchedulersRequestExceptionMonths, body);
                    } catch (e) {
                        console.error('Exception in "requestExceptionMonths": ' + e);
                    }
                    completehandler();
                },
                deleteSpecialEvent: async(schedulerId, specialEventId) => {
                    const body = {schedulerId, specialEventId};
                    await this.serverOperation(this.config.services.plant.plantSchedulersDeleteSpecialEvent, body);
                },
                changeDateException: async(schedulerId, specialEventId, date) => {
                    const body = {schedulerId, specialEventId, date};
                    await this.serverOperation(this.config.services.plant.plantSchedulersChangeDateException, body);
                },
                setDateRange: async(schedulerId, startDate, endDate) => {
                    const body = {schedulerId, startDate, endDate};
                    await this.serverOperation(this.config.services.plant.plantSchedulersSetDateRange, body);
                },
                copySpecialEventProfile: async(schedulerId, specialEventId, destDates) => {
                    const body = {schedulerId, specialEventId, destDates};
                    await this.serverOperation(this.config.services.plant.plantSchedulersCopySpecialEventProfile, body);
                },
                createWeekNDayException: async(schedulerId, weekNDayDetails, startTime, endTime, value, successhandler) => {
                    const body = {schedulerId, weekNDayDetails, startTime, endTime, value, successhandler};
                    await this.serverOperation(this.config.services.plant.plantSchedulersCreateWeekNDayException, body);
                    successhandler();
                },
                changeWeekNDayException: async(schedulerId, specialEventId, weekNDayDetails) => {
                    const body = {schedulerId, specialEventId, weekNDayDetails};
                    await this.serverOperation(this.config.services.plant.plantSchedulersChangeWeekNDayException, body);
                },
                createDateException: async(schedulerId, date, startTime, endTime, value, successhandler) => {
                    const body = {schedulerId, date, startTime, endTime, value, successhandler};
                    await this.serverOperation(this.config.services.plant.plantSchedulersCreateDateException, body);
                    successhandler();
                },
                displaySuccessMessage: (successText) => {
                    this.messageService.addMessage({
                        type: MessageType.success,
                        text: successText
                    });
                },
                displayInfoMessage: (infoText) => {
                    this.messageService.addMessage({
                        type: MessageType.info,
                        text: infoText
                    });
                },
                displayErrorMessage: (errorText) => {
                    this.messageService.addMessage({
                        type: MessageType.error,
                        text: errorText
                    });
                }
            };
        };

        // Register global services
        $.registerService('ba', 'textManager', $.ba.services.TextManager);

        // Initialize main application controller instance
        $.ba.authManager = {
            userLoggedIn: true
        };
    }
}
